import React, { useState, useEffect } from "react";
import { useFeatures } from "../../hooks/useFeatures";
import { useReadOnlyMode } from "../../hooks/useReadOnlyMode";

interface GeneralSectionProps {
  workspace: any;
  workspaceId: string;
  api: any;
}

const GeneralSection: React.FC<GeneralSectionProps> = ({
  workspace,
  workspaceId,
  api,
}) => {
  const { isReadOnly } = useReadOnlyMode();
  const [projectName, setProjectName] = useState<string>("");
  const [originalProjectName, setOriginalProjectName] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showBranding, setShowBranding] = useState<boolean>(true);
  const [originalShowBranding, setOriginalShowBranding] =
    useState<boolean>(true);
  const { has } = useFeatures();

  // Set initial values when workspace data is loaded
  useEffect(() => {
    if (workspace) {
      setProjectName(workspace.name);
      setOriginalProjectName(workspace.name);
      setShowBranding(workspace.show_branding);
      setOriginalShowBranding(workspace.show_branding);
    }
  }, [workspace]);

  const handleSave = async () => {
    if (isReadOnly) return;

    if (
      projectName === originalProjectName &&
      showBranding === originalShowBranding
    ) {
      return; // No changes to save
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage("");

    try {
      const response = await api.put(`/workspaces/${workspaceId}`, {
        name: projectName,
        show_branding: showBranding,
      });
      const updatedWorkspace = response.data || response;

      setOriginalProjectName(projectName);
      setOriginalShowBranding(showBranding);
      setSuccessMessage("Project updated successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      setError(err.message || "Failed to update project");
      // Reset to original name on error
      setProjectName(originalProjectName);
      setShowBranding(originalShowBranding);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">
        General Settings
      </h2>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-xl">
          <p className="text-green-700 dark:text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Project Name - Editable */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
            Project Name
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="flex-1 px-4 py-3 bg-ui-background-primary dark:bg-neutral-800 border border-ui-border-primary dark:border-neutral-700 rounded-xl text-ui-text-primary dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={isSaving || isReadOnly}
              title={isReadOnly ? 'Upgrade to edit project name' : ''}
            />
            <button
              onClick={handleSave}
              disabled={
                isSaving ||
                isReadOnly ||
                !projectName.trim() ||
                (projectName === originalProjectName &&
                  showBranding === originalShowBranding)
              }
              className={`px-4 py-3 rounded-xl font-medium transition-all ${
                isSaving ||
                isReadOnly ||
                !projectName.trim() ||
                (projectName === originalProjectName &&
                  showBranding === originalShowBranding)
                  ? "bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed"
                  : "bg-brand-action hover:bg-brand-action-hover text-white shadow-sm hover:shadow-md"
              }`}
              title={isReadOnly ? 'Upgrade to save changes' : ''}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {/* Project ID - Read-only */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
            Project ID
          </label>
          <input
            type="text"
            value={workspace?.id || ""}
            readOnly
            className="block w-full px-4 py-3 bg-ui-background-primary dark:bg-neutral-800 border border-ui-border-primary dark:border-neutral-700 rounded-xl text-ui-text-primary dark:text-white cursor-not-allowed opacity-60"
          />
        </div>

        {/* Directory Path - Read-only */}
        {workspace?.is_sandbox !== true && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Directory Path
            </label>
            <input
              type="text"
              value={workspace?.directory_path || ""}
              readOnly
              className="block w-full px-4 py-3 bg-ui-background-primary dark:bg-neutral-800 border border-ui-border-primary dark:border-neutral-700 rounded-xl text-ui-text-primary dark:text-white font-mono text-sm cursor-not-allowed opacity-60"
            />
          </div>
        )}

        {/* Created Date - Read-only */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
            Created
          </label>
          <input
            type="text"
            value={
              workspace?.created_at
                ? new Date(workspace.created_at).toLocaleString()
                : ""
            }
            readOnly
            className="block w-full px-4 py-3 bg-ui-background-primary dark:bg-neutral-800 border border-ui-border-primary dark:border-neutral-700 rounded-xl text-ui-text-primary dark:text-white cursor-not-allowed opacity-60"
          />
        </div>

        {/* Show Branding - toggle, gated by feature flag (moved to bottom) */}
        {has && has("disable-branding") && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Show branding
            </label>
            <div className="flex items-center gap-3">
              <input
                id="show-branding"
                type="checkbox"
                checked={showBranding}
                onChange={(e) => setShowBranding(e.target.checked)}
                className="h-4 w-4 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving || isReadOnly}
                title={isReadOnly ? 'Upgrade to change branding settings' : ''}
              />
              <label
                htmlFor="show-branding"
                className="text-neutral-700 dark:text-neutral-300"
              >
                Display Kolega branding on deployed apps
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralSection;
